import React, { useEffect, useMemo, useState } from 'react';
import { Auth } from 'aws-amplify';
import { NavLink } from 'react-router-dom';
import { useAdminCapability, levelAtLeast } from './useAdminCapability';
import { configureLanguages, t, useLanguage } from '../utils/i18n';

const enablePos = String(process.env.REACT_APP_ENABLE_POS || '').toLowerCase() === 'true';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

export default function AdminNavTabs() {
  // Group-based role derived from Cognito claims. Used as the backward-compatible
  // fallback when the /admin/users/me capability is unavailable (loading/error),
  // so existing Admins/PosAdmins keep their current access.
  const [groupRole, setGroupRole] = useState('unknown');
  const [storeMode, setStoreMode] = useState('');

  // Subscribe to language changes so the localized Take Order label re-renders
  // when a staff member flips the switcher on the Take Order page. Return value
  // is unused: the subscription itself is the point.
  useLanguage();

  // Authoritative capability from GET /admin/users/me (role + per-tab permissions).
  const capability = useAdminCapability();

  useEffect(() => {
    const load = async () => {
      try {
        const session = await Auth.currentSession();
        const groups = session.getIdToken().payload['cognito:groups'] || [];
        const isAdmin = Array.isArray(groups) && groups.includes('Admins');
        const isPosAdmin = Array.isArray(groups) && groups.includes('PosAdmins');
        if (isAdmin) {
          setGroupRole('admin');
          return;
        }
        if (isPosAdmin) {
          setGroupRole('pos');
          return;
        }
        setGroupRole('none');
      } catch {
        setGroupRole('none');
      }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        if (!API_BASE_URL) return;
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        const res = await fetch(`${API_BASE_URL}/admin/general`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return;
        const rawMode = data?.settings?.storeMode ?? data?.storeMode ?? '';
        setStoreMode(String(rawMode || '').trim().toUpperCase());
        // Staff-interface languages (take-order-i18n R4.5). Read from the response
        // this component ALREADY fetches — no extra request. Without this, the
        // "Take Order" nav label would stay English on other admin pages while the
        // Take Order page itself rendered Tamil.
        configureLanguages(data?.settings?.i18n ?? data?.i18n);
      } catch {
        // ignore
      }
    };
    load();
  }, []);

  const posNavEnabled = useMemo(() => {
    if (enablePos) return true;
    const mode = String(storeMode || '').trim().toUpperCase();
    return mode === 'POS' || mode === 'HYBRID';
  }, [storeMode]);

  const ordersNavEnabled = useMemo(() => {
    const mode = String(storeMode || '').trim().toUpperCase();
    return mode === 'ECOMM' || mode === 'ECOM';
  }, [storeMode]);

  // Full admin tab catalog, gated by store mode. Each entry is tagged with the
  // Controllable_Tab it belongs to so Staff visibility can be computed from the
  // permission map. (POS configuration is merged into "General Settings".)
  const adminCatalog = useMemo(() => {
    return [
      { to: '/admin/general', label: 'General', tab: 'General Settings' },
      { to: '/admin/reviews', label: 'Reviews', tab: 'Reviews' },
      { to: '/admin/discounts', label: 'Discounts', tab: 'Discounts' },
      { to: '/admin/theme', label: 'Theme', tab: 'Theme' },
      { to: '/admin/dashboard', label: 'Products', tab: 'Products' },
      { to: '/admin/categories', label: 'Categories', tab: 'Categories' },
      { to: '/admin/menu-import', label: 'Menu Import', tab: 'Products' },
      { to: '/admin/locations', label: 'Locations', tab: 'Locations' },
      { to: '/admin/audit', label: 'Audit', tab: 'Audit' },
      { to: '/admin/reports/orders', label: 'Reports', tab: 'Orders/Reports' },
      { to: '/admin/shipments', label: 'Shipments', tab: 'Orders/Reports' },
      ...(ordersNavEnabled
        ? [{ to: '/admin/channels/orders/queue', label: 'Orders Queue', tab: 'Orders/Reports' }]
        : []),
      ...(posNavEnabled
        ? [
            { to: '/admin/channels/pos', label: 'POS', tab: 'General Settings' },
            { to: '/admin/channels/pos/queue', label: 'POS Queue', tab: 'POS Queue' },
            // Only this entry is localized (take-order-i18n scope). Every other tab
            // keeps its plain `label` and is untouched.
            //
            // Gated on the SEPARATE 'Take Order' tab, not 'POS Queue'
            // (order-additions Requirement 2.11). A waiter granted Take Order but
            // not POS Queue sees this entry and not the queue entry above.
            { to: '/admin/channels/pos/take-order', label: 'Take Order', labelKey: 'takeOrder.tabLabel', tab: 'Take Order' },
          ]
        : []),
    ];
  }, [posNavEnabled, ordersNavEnabled]);

  const tabs = useMemo(() => {
    const userMgmtTab = { to: '/admin/users', label: 'User Management', tab: null };

    // Capability-driven path: only when /admin/users/me loaded successfully.
    if (capability.loaded && !capability.error) {
      const role = capability.role;
      if (role === 'Superadmin') {
        return [...adminCatalog, userMgmtTab];
      }
      if (role === 'Admin') {
        // Full admin access, but never the User Management tab.
        return adminCatalog;
      }
      if (role === 'Staff') {
        // Show a controllable tab only when granted at View or Edit. The User
        // Management tab is never added for Staff. Backend still enforces.
        return adminCatalog.filter(
          (t) => t.tab && levelAtLeast(capability.permissions?.[t.tab], 'View')
        );
      }
      return [];
    }

    // Fallback (capability loading or failed): preserve the prior group-based
    // behavior. Fail closed on User Management (never shown without an
    // authoritative Superadmin capability).
    if (groupRole === 'pos') {
      // Neither entry carries a `tab`, deliberately: this branch runs when
      // permissions are UNKNOWN (capability fetch failed), so it cannot filter and
      // instead preserves the prior group-based behavior and lets the server
      // enforce. Take Order is now gated server-side on the 'Take Order' tab, so a
      // legacy PosAdmins-only user resolving to Staff{POS Queue: View} is denied on
      // arrival — exactly as they were before the split, when order creation
      // required POS Queue: Edit that they also lacked.
      return posNavEnabled
        ? [
            { to: '/admin/channels/pos/queue', label: 'POS Queue' },
            { to: '/admin/channels/pos/take-order', label: 'Take Order', labelKey: 'takeOrder.tabLabel' },
          ]
        : [];
    }
    if (groupRole !== 'admin') return [];
    return adminCatalog;
  }, [
    capability.loaded,
    capability.error,
    capability.role,
    capability.permissions,
    adminCatalog,
    groupRole,
    posNavEnabled,
  ]);

  if (!tabs.length) return null;

  return (
    <nav className="admin-tabs" aria-label="Admin navigation">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) => `admin-tab${isActive ? ' active' : ''}`}
        >
          {tab.labelKey ? t(tab.labelKey) : tab.label}
        </NavLink>
      ))}
    </nav>
  );
}
